import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { current: "", next: "", confirm: "" };
const FIELDS = [
  { key: "current", label: "Current password", testid: "change-password-current" },
  { key: "next", label: "New password (at least 10 characters)", testid: "change-password-new" },
  { key: "confirm", label: "Confirm new password", testid: "change-password-confirm" },
];

const errorText = (d) =>
  typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x?.msg).filter(Boolean).join(" ") : "Could not change password.";

export default function ChangePassword({ token, onChanged }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 10) return toast.error("New password must be at least 10 characters.");
    if (form.next !== form.confirm) return toast.error("The new passwords don't match.");
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/change-password`,
        { current_password: form.current, new_password: form.next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onChanged(data.token);
      toast.success("Password changed. Use your new password next time you sign in.");
      setForm(EMPTY);
      setOpen(false);
    } catch (err) {
      toast.error(errorText(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="admin-change-password-button"
        className="inline-flex items-center gap-2 text-sm"
        style={{ color: "var(--rose-mocha-deep)" }}
      >
        <KeyRound size={16} /> Change password
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="change-password-dialog" className="max-w-md rounded-3xl font-body"
          style={{ backgroundColor: "var(--bg-primary)", border: "1px solid var(--border-light)" }}>
          <DialogTitle className="font-serif text-2xl" style={{ color: "var(--text-primary)" }}>Change password</DialogTitle>
          <DialogDescription style={{ color: "var(--text-secondary)" }}>
            Only you will know your new password.
          </DialogDescription>
          <form onSubmit={submit} className="flex flex-col gap-5 mt-2">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <label className="text-xs tracking-[0.15em] uppercase" style={{ color: "var(--rose-mocha-deep)" }}>{f.label}</label>
                <input type="password" data-testid={f.testid} value={form[f.key]} autoComplete="new-password"
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="w-full bg-transparent py-2.5 outline-none"
                  style={{ borderBottom: "1px solid var(--border-light)", color: "var(--text-primary)" }} />
              </div>
            ))}
            <button type="submit" data-testid="change-password-submit" disabled={busy}
              className="self-start rounded-full px-7 py-3 text-sm font-medium disabled:opacity-60"
              style={{ backgroundColor: "var(--mocha-brown)", color: "#FBF6F3" }}>
              {busy ? "Saving…" : "Save new password"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
